/**
 * Encrypted backup package — SPEC §8.4 / FR-DEV backup (H7).
 * Argon2id wrap via WASM vault; ciphertext downloadable by user only.
 */

import { wrapVaultKey, unlockVaultKey } from "@veto/crypto-bridge";
import { VetoError } from "@veto/domain";
import { stripVaultKeyFromWrapped } from "../vaultCrypto";

export interface BackupPackageV1 {
  v: 1;
  type: "veto.backup";
  createdAt: number;
  /** Argon2id-wrapped random backup key (no vault_key_b64). */
  wrappedJson: string;
  /** AES-GCM ciphertext of JSON payload, base64(iv||ct), keyed by unlocked backup key. */
  ciphertextB64: string;
}

function b64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function unb64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function asBuf(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export async function createBackupPackage(
  passphrase: string,
  payload: unknown,
): Promise<BackupPackageV1> {
  const wrappedRaw = wrapVaultKey(passphrase);
  const { durableWrappedJson, vaultKeyB64 } = stripVaultKeyFromWrapped(wrappedRaw);
  if (!vaultKeyB64) throw new Error("backup key missing from wrap");
  const keyBytes = unb64(vaultKeyB64);
  const key = await crypto.subtle.importKey("raw", asBuf(keyBytes), "AES-GCM", false, ["encrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const pt = new TextEncoder().encode(JSON.stringify(payload));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, pt),
  );
  const packed = new Uint8Array(iv.length + ct.length);
  packed.set(iv, 0);
  packed.set(ct, iv.length);
  return {
    v: 1,
    type: "veto.backup",
    createdAt: Date.now(),
    wrappedJson: durableWrappedJson,
    ciphertextB64: b64(packed),
  };
}

export async function openBackupPackage(
  passphrase: string,
  pkg: BackupPackageV1,
): Promise<unknown> {
  if (pkg.v !== 1 || pkg.type !== "veto.backup") {
    throw new VetoError("VALIDATION_FAILED", "Unsupported backup file");
  }
  let vaultKeyB64: string;
  try {
    vaultKeyB64 = unlockVaultKey(pkg.wrappedJson, passphrase);
  } catch (cause) {
    throw new VetoError("CRYPTO_OPEN_FAILED", "Wrong backup passphrase — package stays sealed", {
      cause,
    });
  }
  try {
    const keyBytes = unb64(vaultKeyB64);
    const packed = unb64(pkg.ciphertextB64);
    const iv = packed.subarray(0, 12);
    const ct = packed.subarray(12);
    const key = await crypto.subtle.importKey("raw", asBuf(keyBytes), "AES-GCM", false, ["decrypt"]);
    const pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: asBuf(iv) },
      key,
      asBuf(ct),
    );
    return JSON.parse(new TextDecoder().decode(pt));
  } catch (cause) {
    throw new VetoError("CRYPTO_OPEN_FAILED", "Wrong backup passphrase — package stays sealed", {
      cause,
    });
  }
}

export function isBackupPackageV1(value: unknown): value is BackupPackageV1 {
  if (!value || typeof value !== "object") return false;
  const o = value as Record<string, unknown>;
  return o.v === 1 && o.type === "veto.backup" && typeof o.wrappedJson === "string" && typeof o.ciphertextB64 === "string";
}

export function downloadBackupJson(pkg: BackupPackageV1, filename = "veto-backup.json"): void {
  const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
